import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearAuth,
  getStoredAuth,
  loginRequest,
  logoutRequest,
  meRequest,
  refreshRequest,
  storeAuth,
} from '../services/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const initializeAuth = useCallback(async () => {
    const stored = getStoredAuth();

    if (!stored.accessToken || !stored.refreshToken) {
      setIsLoadingAuth(false);
      return;
    }

    try {
      const meResponse = await meRequest(stored.accessToken);
      setUser(meResponse?.data?.user || null);
      setAccessToken(stored.accessToken);
      setRefreshToken(stored.refreshToken);
    } catch (error) {
      try {
        const refreshResponse = await refreshRequest(stored.refreshToken);
        const newAccessToken = refreshResponse?.data?.auth?.access_token || '';
        const newRefreshToken = refreshResponse?.data?.auth?.refresh_token || '';

        if (!newAccessToken || !newRefreshToken) {
          throw new Error('Invalid refresh response');
        }

        storeAuth(newAccessToken, newRefreshToken);
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);

        const meResponse = await meRequest(newAccessToken);
        setUser(meResponse?.data?.user || null);
      } catch (refreshError) {
        clearAuth();
        setUser(null);
        setAccessToken('');
        setRefreshToken('');
      }
    }

    setIsLoadingAuth(false);
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = useCallback(async ({ login, password, rememberMe }) => {
    const response = await loginRequest({
      login,
      password,
      remember_me: rememberMe,
    });

    const newAccessToken = response?.data?.auth?.access_token || '';
    const newRefreshToken = response?.data?.auth?.refresh_token || '';

    if (!newAccessToken || !newRefreshToken) {
      throw new Error('Login response missing token payload');
    }

    storeAuth(newAccessToken, newRefreshToken);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    const meResponse = await meRequest(newAccessToken);
    setUser(meResponse?.data?.user || null);

    return meResponse;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (accessToken) {
        await logoutRequest(accessToken);
      }
    } catch (error) {
      // Ignore logout endpoint failure and clear local auth state.
    }

    clearAuth();
    setUser(null);
    setAccessToken('');
    setRefreshToken('');
  }, [accessToken]);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      isLoadingAuth,
      isAuthenticated: Boolean(user && accessToken),
      isAdmin: user?.is_admin === true || String(user?.role_key || '').toLowerCase() === 'admin',
      userBranchId: user?.branch_id ?? null,
      userBranchName: user?.branch_name ?? null,
      login,
      logout,
    }),
    [user, accessToken, refreshToken, isLoadingAuth, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
